(() => {
  if (customElements.get('shipping-info')) return;

  let countryPromise;
  function detectCountry() {
    if (!countryPromise) {
      const root = window.Shopify?.routes?.root || '/';
      countryPromise = fetch(root + 'browsing_context_suggestions.json?country[enabled]=true', {
        credentials: 'same-origin',
        signal: AbortSignal.timeout(4000)
      }).then(response => {
        if (!response.ok) throw new Error('Country detection failed');
        return response.json();
      }).then(data => {
        const country = data.detected_values?.country?.handle;
        return typeof country === 'string' && /^[A-Z]{2}$/i.test(country) ? country.toUpperCase() : null;
      })
        .catch(() => null);
    }
    return countryPromise;
  }

  customElements.define('shipping-info', class extends HTMLElement {
    connectedCallback() {
      if (!this.querySelector('button') || !this.querySelector('[popover]')) {
        requestAnimationFrame(() => {
          if (this.isConnected) this.connectedCallback();
        });
        return;
      }
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      const button = this.querySelector('button');
      const panel = this.querySelector('[popover]');
      const id = 'shipping-info-' + crypto.randomUUID();
      panel.id = id;
      button.setAttribute('aria-describedby', id);
      button.setAttribute('aria-controls', id);

      const close = () => {
        clearTimeout(this.closeTimer);
        if (panel.matches(':popover-open')) panel.hidePopover();
      };
      const position = () => {
        const rect = button.getBoundingClientRect();
        const width = panel.offsetWidth;
        const height = panel.offsetHeight;
        const left = Math.max(12, Math.min(rect.left, innerWidth - width - 12));
        const preferredTop = rect.top - height - 8;
        const top = preferredTop >= 12 ? preferredTop : rect.bottom + 8;
        panel.style.left = left + 'px';
        panel.style.top = Math.max(12, Math.min(top, innerHeight - height - 12)) + 'px';
      };
      const open = () => {
        clearTimeout(this.closeTimer);
        if (!panel.matches(':popover-open')) panel.showPopover();
        position();
      };

      button.addEventListener('click', () => {
        if (this.pinned) {
          this.pinned = false;
          close();
        } else {
          open();
          this.pinned = true;
        }
      }, options);
      this.addEventListener('pointerenter', event => {
        if (event.pointerType === 'mouse') open();
      }, options);
      this.addEventListener('pointerleave', event => {
        if (event.pointerType === 'mouse' && !this.pinned && !this.contains(document.activeElement)) {
          this.closeTimer = setTimeout(close, 200);
        }
      }, options);
      button.addEventListener('focus', open, options);
      this.addEventListener('focusout', event => {
        if (!this.contains(event.relatedTarget)) close();
      }, options);
      panel.addEventListener('toggle', () => {
        const isOpen = panel.matches(':popover-open');
        button.setAttribute('aria-expanded', String(isOpen));
        if (!isOpen) this.pinned = false;
      }, options);
      window.addEventListener('resize', close, options);
      document.addEventListener('scroll', event => {
        if (!panel.contains(event.target)) close();
      }, { ...options, capture: true });

      const controller = this.controller;
      detectCountry().then(country => {
        if (!this.isConnected || this.controller !== controller) return;
        const rates = JSON.parse(this.dataset.rates || '{}');
        const supported = Boolean(country && Object.hasOwn(rates, country));
        const shipping = supported ? rates[country] : rates.DE;
        const summary = this.closest('[data-shipping-summary]');
        if (summary && Number.isSafeInteger(shipping) && shipping >= 0) {
          const subtotal = Number(summary.dataset.cartTotal);
          const formatter = new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
            style: 'currency',
            currency: summary.dataset.currency || 'EUR'
          });
          const price = summary.querySelector('[data-shipping-price]');
          const total = summary.querySelector('[data-total-with-shipping]');
          if (price) price.textContent = formatter.format(shipping / 100);
          if (total && Number.isSafeInteger(subtotal)) total.textContent = formatter.format((subtotal + shipping) / 100);
          summary.dataset.shippingCountry = supported ? country : 'DE';
          summary.dataset.shippingDetection = supported ? 'detected' : 'fallback';
        }
        this.hidden = supported && country === 'DE';
      });
    }

    disconnectedCallback() {
      clearTimeout(this.closeTimer);
      this.controller?.abort();
      this.controller = null;
      this.pinned = false;
    }
  });
})();

